import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Loader2, CheckCircle2, AlertCircle, Filter, Save, Mail, MapPin, Briefcase, Globe, Database, Sparkles, Download, ArrowLeft, RotateCcw, BarChart3, X, FlaskConical } from 'lucide-react';
import { AutoImportPanel } from '@/components/imports/AutoImportPanel';
import { toast } from 'sonner';

const API = '/api';

// Flat bucket taxonomy — sub-category is folded into bucket choice.
// `bucket` + `sub_category` derive a single display key the user picks.
const BUCKETS = [
  { key: 'all', label: 'All', color: 'bg-slate-100 text-slate-700' },
  { key: 'biotech', label: 'Biotech', color: 'bg-emerald-100 text-emerald-800' },
  { key: 'delivery_tech', label: 'Delivery Tech', color: 'bg-indigo-100 text-indigo-800' },
  { key: 'dtx', label: 'DTx', color: 'bg-cyan-100 text-cyan-800' },
  { key: 'platform', label: 'Platform', color: 'bg-fuchsia-100 text-fuchsia-800' },
  { key: 'university', label: 'University', color: 'bg-violet-100 text-violet-800' },
  { key: 'pharma', label: 'Pharma', color: 'bg-sky-100 text-sky-800' },
  { key: 'distributor', label: 'Distributor', color: 'bg-teal-100 text-teal-800' },
  { key: 'investor', label: 'Investor', color: 'bg-amber-100 text-amber-800' },
  { key: 'cro_cdmo', label: 'CRO/CDMO', color: 'bg-rose-100 text-rose-800' },
  { key: 'service', label: 'Service', color: 'bg-rose-100 text-rose-800' },
  { key: 'unclear', label: 'Unclear', color: 'bg-slate-100 text-slate-500' },
  { key: 'skip_prompt', label: 'Skip · prompt', color: 'bg-orange-100 text-orange-800' },
  { key: 'excluded_by_ai', label: 'Excluded by AI', color: 'bg-orange-100 text-orange-700' },
  { key: 'excluded', label: 'Excluded', color: 'bg-rose-100 text-rose-700' },
  { key: 'approved', label: 'Approved', color: 'bg-emerald-100 text-emerald-700' },
  { key: 'unapproved', label: 'Unapproved', color: 'bg-slate-200 text-slate-600' },
  // Feb 2026 · user request — one-click surfacing of biotech-looking rows
  // that were LLM-misclassified as pharma/academic/distributor and got
  // stuck in staging because they carry neither an email nor a fuzzy
  // match. Companion of the "Reclassify → biotech + approve" bulk
  // action that appears when this chip is active.
  { key: 'stuck_no_email_no_match', label: '🚫 Stuck (no email + no match)', color: 'bg-rose-100 text-rose-700' },
];

// Bucket → which collection it lands in (display key ↔ data target)
const BUCKET_TARGETS = {
  biotech: 'biotech_companies',
  delivery_tech: 'biotech_companies (NDD)',
  dtx: 'biotech_companies (DTx)',
  platform: 'biotech_companies (Platform)',
  university: '/universities',
  pharma: 'company_profiles',
  distributor: 'company_profiles (Distributor)',
  investor: 'investors',
  cro_cdmo: 'EXCLUDED',
  service: 'EXCLUDED',
  unclear: 'EXCLUDED',
  other: 'EXCLUDED',
};

// Compute a single display bucket from row's (bucket, sub_category)
const displayBucket = (r) => {
  const b = r.proposed_bucket;
  const s = r.sub_category;
  if (b === 'biotech' && s === 'ndds') return 'delivery_tech';
  if (b === 'biotech' && s === 'dtx') return 'dtx';
  if (b === 'biotech' && s === 'platform') return 'platform';
  if (b === 'biotech' && s === 'university') return 'university';
  if (b === 'academic') return 'university';
  return b || 'unclear';
};

// Reverse mapping: when user picks a display bucket, what (bucket, sub_category) do we save?
const splitDisplayBucket = (display) => {
  switch (display) {
    case 'biotech': return { proposed_bucket: 'biotech', sub_category: 'general_biotech' };
    case 'delivery_tech': return { proposed_bucket: 'biotech', sub_category: 'ndds' };
    case 'dtx': return { proposed_bucket: 'biotech', sub_category: 'dtx' };
    case 'platform': return { proposed_bucket: 'biotech', sub_category: 'platform' };
    case 'university': return { proposed_bucket: 'biotech', sub_category: 'university' };
    default: return { proposed_bucket: display, sub_category: null };
  }
};

export default function ConferenceImportPage() {
  const { tag } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const importTag = tag || 'chinabio_2026';

  const [items, setItems] = useState([]);
  const [bucketCounts, setBucketCounts] = useState({});
  const [subCounts, setSubCounts] = useState({});
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [committing, setCommitting] = useState(false);
  const [search, setSearch] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [detailRow, setDetailRow] = useState(null);  // row whose detail modal is open
  const [emailFilter, setEmailFilter] = useState('all'); // 'all' | 'with' | 'without'
  const [totalParsed, setTotalParsed] = useState(0);
  const [committedCount, setCommittedCount] = useState(0);
  const [recoveryRunning, setRecoveryRunning] = useState(false);
  // Feb 2026 · deep-link focus support. Landing on
  //   /imports/{tag}?focus={row_id}
  // (e.g. from the Debug popover on Global Search) scrolls the target
  // row into view and briefly highlights it in amber. `focusedId`
  // tracks WHICH row to highlight; `rowRefs` maps row-id → DOM node so
  // we can scroll to it once the fetch completes.
  const [focusedId, setFocusedId] = useState(searchParams.get('focus') || null);
  const rowRefs = useRef({});
  // Feb 2026 · row-multi-select for the "🚫 Stuck (no email + no match)"
  // review workflow. Only rendered when that chip is active — pairs
  // with the "Reclassify → biotech + approve" bulk action button.
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Feb 2026 v2 · dropdown-driven bulk approve. `bulkBucket = ''`
  // means "keep each row's AI-classified bucket, just approve" (the
  // user-requested default so investors/distributors/academics/pharma
  // all get committed to their respective collections). Any specific
  // value (biotech/pharma/academic/distributor/investor) forces a
  // reclassify + approve.
  const [bulkBucket, setBulkBucket] = useState('');
  const [bulkApproving, setBulkApproving] = useState(false);
  // Distribution modal: shows the full bucket × sub_category × committed × email matrix
  // so the user can answer "where did my biotechs-without-email actually go?"
  const [distOpen, setDistOpen] = useState(false);
  const [distLoading, setDistLoading] = useState(false);
  const [distData, setDistData] = useState(null);

  const openDistribution = async () => {
    setDistOpen(true);
    setDistLoading(true);
    try {
      const res = await axios.get(`${API}/imports/${importTag}/distribution`, { withCredentials: true });
      setDistData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load distribution');
    } finally {
      setDistLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/imports/${importTag}/preview`, { withCredentials: true });
      setItems(res.data.items || []);
      setBucketCounts(res.data.bucket_counts || {});
      setSubCounts(res.data.sub_counts || {});
      setTotalParsed(res.data.total_parsed || 0);
      setCommittedCount(res.data.committed_count || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load import');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [importTag]);

  // Companion asset block staged under the same tag (asset-level master sheets).
  const [assetJob, setAssetJob] = useState(null);
  useEffect(() => {
    axios.get(`${API}/asset-imports`, { withCredentials: true })
      .then(r => setAssetJob((r.data?.items || []).find(i => i.import_tag === importTag) || null))
      .catch(() => setAssetJob(null));
  }, [importTag, committedCount]);

  // Feb 2026 · one-shot deep-link focus effect. Once the items list
  // has been loaded, find the focused row, scroll it into view, and
  // add a temporary 'ring' class (cleared after ~4s). Also clears the
  // `?focus=` query param so a page refresh doesn't re-trigger.
  useEffect(() => {
    if (!focusedId || loading || items.length === 0) return;
    const target = items.find(r => r.id === focusedId);
    if (!target) return;
    // Pre-fill the search box with the row name so it's actually
    // visible even if the current filter chip would hide it.
    if (search === '') setSearch(target.name || '');
    // Wait one paint for the row to render, then scroll + flash.
    const t = setTimeout(() => {
      const node = rowRefs.current[focusedId];
      if (node) {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      // Clear the highlight (and the URL param) after 4s so the row
      // returns to normal styling on the next filter change.
      const clear = setTimeout(() => {
        setFocusedId(null);
        // eslint-disable-next-line
        const next = new URLSearchParams(searchParams);
        next.delete('focus');
        setSearchParams(next, { replace: true });
      }, 4000);
      return () => clearTimeout(clear);
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedId, loading, items]);

  // Feb 2026 · reset row selection whenever the filter/search changes
  // so stale IDs from a previous view don't leak into the bulk action.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [filter, search, emailFilter]);

  const filteredItems = useMemo(() => {
    let arr = items;
    if (filter === 'skip_prompt') {
      arr = arr.filter(r => !r.approved && (
        r.academic_filter_verdict === 'fail' ||
        r.ndds_filter_verdict === 'fail' ||
        r.dtx_filter_verdict === 'fail' ||
        r.biotech_filter_verdict === 'fail' ||
        r.pharma_filter_verdict === 'fail'
      ));
    } else if (filter === 'excluded_by_ai') {
      arr = arr.filter(r => r.exclusion_filter_verdict === 'exclude');
    } else if (filter === 'excluded') {
      arr = arr.filter(r => ['cro_cdmo', 'service', 'unclear'].includes(r.proposed_bucket));
    } else if (filter === 'unapproved') {
      arr = arr.filter(r => !r.approved);
    } else if (filter === 'stuck_no_email_no_match') {
      // Only rows tagged by Pass 5a as stuck for the specific reason.
      // Excludes committed / already-approved / other unapproved-reason rows.
      arr = arr.filter(r =>
        !r.approved
        && !r.committed
        && r.unapproved_reason === 'no_email_and_no_match'
      );
    } else if (filter === 'approved') {
      arr = arr.filter(r => r.approved);
    } else if (filter !== 'all') {
      arr = arr.filter(r => displayBucket(r) === filter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter(r => (r.name || '').toLowerCase().includes(q) || (r.country || '').toLowerCase().includes(q));
    }
    if (emailFilter === 'with') {
      arr = arr.filter(r => !!(r.email || '').trim());
    } else if (emailFilter === 'without') {
      arr = arr.filter(r => !(r.email || '').trim());
    }
    return arr;
  }, [items, filter, search, emailFilter]);

  // Recompute bucket counts client-side using the flat display taxonomy
  const displayBucketCounts = useMemo(() => {
    const counts = {};
    let skip_prompt = { total: 0, approved: 0 };
    let excluded_by_ai = { total: 0, approved: 0 };
    let excluded = { total: 0, approved: 0 };
    let approved_total = { total: 0, approved: 0 };
    let unapproved = { total: 0, approved: 0 };
    let stuck_no_email_no_match = { total: 0, approved: 0 };
    for (const r of items) {
      const k = displayBucket(r);
      const c = counts[k] || (counts[k] = { total: 0, approved: 0 });
      c.total += 1;
      if (r.approved) c.approved += 1;
      if (!r.approved && (r.academic_filter_verdict === 'fail' || r.ndds_filter_verdict === 'fail' || r.dtx_filter_verdict === 'fail' || r.biotech_filter_verdict === 'fail' || r.pharma_filter_verdict === 'fail')) {
        skip_prompt.total += 1;
      }
      if (r.exclusion_filter_verdict === 'exclude') {
        excluded_by_ai.total += 1;
      }
      if (['cro_cdmo', 'service', 'unclear'].includes(r.proposed_bucket)) {
        excluded.total += 1;
      }
      if (r.approved) {
        approved_total.total += 1;
      }
      if (!r.approved) {
        unapproved.total += 1;
      }
      if (!r.approved && !r.committed && r.unapproved_reason === 'no_email_and_no_match') {
        stuck_no_email_no_match.total += 1;
      }
    }
    counts.skip_prompt = skip_prompt;
    counts.excluded_by_ai = excluded_by_ai;
    counts.excluded = excluded;
    counts.approved = approved_total;
    counts.unapproved = unapproved;
    counts.stuck_no_email_no_match = stuck_no_email_no_match;
    return counts;
  }, [items]);

  const updateRow = async (id, patch) => {
    setSavingId(id);
    setItems(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
    try {
      await axios.patch(`${API}/imports/staging/${id}`, patch, { withCredentials: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
      fetchData();
    } finally {
      setSavingId(null);
    }
  };

  const downloadXlsx = async (onlyApproved) => {
    try {
      const res = await axios.get(`${API}/imports/${importTag}/export?only_approved=${onlyApproved ? 1 : 0}`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `${importTag}_${onlyApproved ? 'approved' : 'all'}_${ts}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${onlyApproved ? 'approved' : 'all'} rows`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Download failed');
    }
  };

  // One-time recovery for the historical `target_collection` silent-skip bug.
  // Calls /recommit (resets the stuck rows + derives target from proposed_bucket),
  // then immediately calls /commit (actually writes records). Surfaces a single
  // toast with the combined results. Idempotent — safe to click multiple times.
  const recoverStuckCommits = async () => {
    if (!window.confirm(
      'This will re-run the commit step on rows that were silently skipped by an earlier bug.\n\n' +
      '✓ Reuses your existing AI classifications — NO new Claude/Perplexity calls\n' +
      '✓ Safe: existing records get enriched, never duplicated\n' +
      '✓ Idempotent: clicking twice does no harm\n\n' +
      'Proceed?'
    )) return;
    setRecoveryRunning(true);
    try {
      const r1 = await axios.post(
        `${API}/imports/${importTag}/recommit`,
        {},
        { withCredentials: true, timeout: 120000 }
      );
      const reset = r1.data?.rows_reset_to_uncommitted ?? 0;
      const routable = r1.data?.now_routable ?? 0;
      if (reset === 0) {
        toast.info('No stuck rows found for this import — nothing to recover.');
        setRecoveryRunning(false);
        return;
      }
      const r2 = await axios.post(
        `${API}/imports/${importTag}/commit`,
        {},
        { withCredentials: true, timeout: 600000 }
      );
      const c = r2.data || {};
      const newBio = (c.imported_biotech || 0) + (c.imported_academic || 0);
      const newPharma = c.imported_pharma || 0;
      const newInv = c.imported_investor || 0;
      const enrichB = c.enriched_biotech || 0;
      const enrichP = c.enriched_pharma || 0;
      toast.success(
        `Recovery complete · Reset ${reset} stuck rows · Routable ${routable}. ` +
        `Created ${newBio} biotechs, ${newPharma} pharma, ${newInv} investors. ` +
        `Enriched ${enrichB} biotechs + ${enrichP} pharma.`,
        { duration: 12000 }
      );
      fetchData();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || 'Recovery failed';
      toast.error(`Recovery failed: ${detail}`);
    } finally {
      setRecoveryRunning(false);
    }
  };

  const downloadMissingBiotechs = async () => {
    try {
      const res = await axios.get(
        `${API}/imports/${importTag}/missing-contacts.xlsx`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `${importTag}_missing_contacts_${ts}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Downloaded companies with no contact email (sheet + CRM checked)');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No matching rows');
    }
  };

  const downloadCurrentView = async () => {
    const qs = new URLSearchParams();
    qs.set('uncommitted_only', '1');
    if (filter && filter !== 'all') qs.set('display_bucket', filter);
    if (emailFilter === 'with') qs.set('email_filter', 'with');
    else if (emailFilter === 'without') qs.set('email_filter', 'without');
    if (search.trim()) qs.set('search', search.trim());
    try {
      const res = await axios.get(`${API}/imports/${importTag}/export?${qs.toString()}`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      const tab = filter && filter !== 'all' ? filter : 'all';
      a.download = `${importTag}_${tab}_${emailFilter !== 'all' ? emailFilter + 'Email_' : ''}${ts}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filteredItems.length} rows from current view`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No matching rows');
    }
  };

  const toggleAll = (val) => {
    const ids = filteredItems.map(r => r.id);
    setItems(prev => prev.map(r => ids.includes(r.id) ? { ...r, approved: val } : r));
    Promise.all(ids.map(id => axios.patch(`${API}/imports/staging/${id}`, { approved: val }, { withCredentials: true })))
      .then(() => toast.success(`${val ? 'Approved' : 'Unapproved'} ${ids.length} rows`, {
        action: {
          label: 'Undo',
          onClick: () => {
            const restoreVal = !val;
            setItems(prev => prev.map(r => ids.includes(r.id) ? { ...r, approved: restoreVal } : r));
            Promise.all(ids.map(id => axios.patch(`${API}/imports/staging/${id}`, { approved: restoreVal }, { withCredentials: true })))
              .then(() => toast.success(`Reverted — ${restoreVal ? 'approved' : 'unapproved'} ${ids.length} rows`))
              .catch(() => fetchData());
          },
        },
        duration: 8000,
      }))
      .catch(() => fetchData());
  };

  const [confirmCommitOpen, setConfirmCommitOpen] = useState(false);

  const handleCommit = () => setConfirmCommitOpen(true);

  const doCommit = async () => {
    setConfirmCommitOpen(false);
    setCommitting(true);
    try {
      const res = await axios.post(`${API}/imports/${importTag}/commit`, {}, { withCredentials: true, timeout: 180000 });
      const c = res.data;
      const summary = [
        c.imported_biotech ? `${c.imported_biotech} new biotechs` : null,
        c.imported_pharma ? `${c.imported_pharma} new pharmas` : null,
        c.imported_investor ? `${c.imported_investor} new investors` : null,
        c.imported_academic ? `${c.imported_academic} new academic` : null,
        c.imported_distributor ? `${c.imported_distributor} new distributors` : null,
        c.enriched_biotech ? `${c.enriched_biotech} biotechs enriched` : null,
        c.enriched_pharma ? `${c.enriched_pharma} pharmas enriched` : null,
        // Feb 2026 v2 · orphan-asset relink surface — post-commit
        // fuzzy pass (RapidFuzz ≥ 92) linked N previously-orphan
        // assets to the newly-committed companies.
        c.relinked_orphan_assets ? `🔗 ${c.relinked_orphan_assets} orphan assets relinked` : null,
        c.errors ? `${c.errors} errors` : null,
      ].filter(Boolean).join(', ');
      const committedTotal = (c.imported_biotech || 0) + (c.imported_pharma || 0) + (c.imported_investor || 0)
        + (c.imported_academic || 0) + (c.imported_distributor || 0) + (c.enriched_biotech || 0) + (c.enriched_pharma || 0);
      if (c.skipped) {
        toast.warning(
          `${c.skipped} approved row${c.skipped === 1 ? '' : 's'} NOT committed — CRO/CDMO, Service and Unclear rows have no CRM destination (they are out of licensing scope). ` +
          `Change their bucket to Biotech / Pharma / Distributor / Investor if they really belong in the CRM.`,
          { duration: 14000 },
        );
      }
      if (committedTotal > 0) toast.success(`Commit complete — ${summary}`);
      else if (!c.skipped) toast.info('Nothing to commit — no approved, uncommitted rows.');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Commit failed');
    } finally {
      setCommitting(false);
    }
  };

  const totalRows = items.length;
  const approvedCount = items.filter(r => r.approved).length;
  const NO_DESTINATION = ['cro_cdmo', 'service', 'unclear'];
  const approvedNoDest = items.filter(r => r.approved && !r.committed && NO_DESTINATION.includes(r.proposed_bucket)).length;
  const committableCount = items.filter(r => r.approved && !r.committed && !NO_DESTINATION.includes(r.proposed_bucket)).length;
  const enrichCount = items.filter(r => r.action === 'enrich').length;
  const importCount = items.filter(r => r.action === 'import' && r.approved).length;

  return (
    <div className="p-6 md:p-8 max-w-[1400px] mx-auto" data-testid="conference-import-page">
      {/* Header */}
      <div className="mb-5">
        <Link to="/imports" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-sky-700 mb-2" data-testid="back-to-imports">
          <ArrowLeft className="w-3 h-3" /> All conference imports
        </Link>
        <div className="flex items-center justify-between gap-4 mb-1 flex-wrap">
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-sky-600" />
            <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Conference Import — {importTag.replace(/_/g, ' ').toUpperCase()}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => downloadXlsx(true)} data-testid="download-approved-btn">
              <Download className="w-3.5 h-3.5 mr-1" /> Approved.xlsx
            </Button>
            <Button size="sm" variant="outline" onClick={() => downloadXlsx(false)} data-testid="download-all-btn">
              <Download className="w-3.5 h-3.5 mr-1" /> Full.xlsx
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={downloadMissingBiotechs}
              data-testid="download-missing-biotechs-btn"
              className="border-amber-300 text-amber-800 hover:bg-amber-50"
              title="Every company in this sheet with no contact email — neither in the sheet nor on its CRM record — with the sheet's contact hints (named BD lead, CEO, delegates, contact form, LinkedIn) so you can find the address"
            >
              <Download className="w-3.5 h-3.5 mr-1" /> Missing contacts (.xlsx)
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={recoverStuckCommits}
              disabled={recoveryRunning}
              data-testid="recover-stuck-btn"
              className="border-rose-300 text-rose-700 hover:bg-rose-50"
              title="One-time recovery: re-runs commit on rows that were silently skipped by the older target_collection bug. Reuses existing AI classifications — no new Claude/Perplexity calls. Safe to click; reports counts and refreshes."
            >
              {recoveryRunning ? (
                <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Recovering…</>
              ) : (
                <><RotateCcw className="w-3.5 h-3.5 mr-1" /> Recover stuck commits</>
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                if (!window.confirm(
                  'Re-apply the approval rule to all uncommitted rows?\n\n' +
                  'Current rule:\n' +
                  '  • Biotechs (any sub-type) → always approved (with or without email).\n' +
                  '  • Pharma / Academic / Distributor → approved if email OR fuzzy-match.\n' +
                  '  • Investor → approved only if email.\n' +
                  '  • CRO / Service / Unclear → never approved.\n\n' +
                  'This is a SAFE re-stamp on uncommitted rows only. Already-committed rows are untouched. ' +
                  'No new AI calls. Use this after the rule has been updated to re-classify existing staging.'
                )) return;
                try {
                  const r = await axios.post(
                    `${API}/imports/${importTag}/reapply-approval-rule`,
                    {},
                    { withCredentials: true },
                  );
                  toast.success(r.data?.message || `Re-applied. ${r.data?.after_approved_pending || 0} pending.`);
                  await fetchData();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Re-apply failed');
                }
              }}
              data-testid="reapply-approval-rule-btn"
              className="border-violet-300 text-violet-800 hover:bg-violet-50"
              title="Re-runs the approval rule (Pass 5a) on uncommitted rows. Use after the rule changes — for example, when 'biotechs always approved' becomes the new policy, click this to flip existing emailless biotechs to approved."
            >
              <Sparkles className="w-3.5 h-3.5 mr-1" /> Re-apply approval rule
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                if (!window.confirm(
                  'Revert silent pharma → Commercial Partner flips?\n\n' +
                  'During earlier commits of this import, some `pharma` profiles were silently moved to ' +
                  '`regional_distributor` (Commercial Partner) — taking their pharma_contacts with them and ' +
                  'dropping the Pharma KPI count. This action moves them back to `pharma` and tags them for ' +
                  'manual review.\n\n' +
                  'Their `met_at` history is preserved. The "Change category" dropdown on each profile lets ' +
                  'you re-classify them individually if needed.'
                )) return;
                try {
                  const r = await axios.post(
                    `${API}/imports/${importTag}/revert-distributor-flips`,
                    {},
                    { withCredentials: true },
                  );
                  toast.success(r.data?.message || `Reverted ${r.data?.reverted || 0} profiles`);
                  await fetchData();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Revert failed');
                }
              }}
              data-testid="revert-distributor-flips-btn"
              className="border-rose-300 text-rose-800 hover:bg-rose-50"
              title="Undoes the silent pharma → Commercial Partner flips caused by this conference commit. Restores the Pharma KPI count and the lost pharma_contacts."
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Revert distributor flips
            </Button>
          </div>
        </div>
        <div className="mt-3" data-testid="import-audit-panel">
          <div className="grid grid-cols-1 md:grid-cols-7 gap-2 items-stretch">
            {/* Parsed */}
            <div className="md:col-span-2 bg-white border border-slate-200 rounded-md px-3 py-2.5">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Parsed from Excel</div>
              <div className="text-2xl font-bold text-slate-900 tabular-nums" data-testid="audit-total-parsed">{totalParsed.toLocaleString()}</div>
              <div className="text-[10px] text-slate-400">attendees in upload</div>
            </div>
            <div className="hidden md:flex items-center justify-center text-2xl text-slate-300 font-light">=</div>
            {/* Committed */}
            <div className="md:col-span-2 bg-emerald-50/60 border border-emerald-200 rounded-md px-3 py-2.5">
              <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Committed</div>
              <div className="text-2xl font-bold text-emerald-700 tabular-nums" data-testid="audit-committed">{committedCount.toLocaleString()}</div>
              <div className="text-[10px] text-emerald-600/80">already in CRM</div>
            </div>
            <div className="hidden md:flex items-center justify-center text-2xl text-slate-300 font-light">+</div>
            {/* Remaining */}
            <div className="md:col-span-1 bg-sky-50/60 border border-sky-200 rounded-md px-3 py-2.5">
              <div className="text-[10px] uppercase tracking-wider text-sky-700 font-semibold">Remaining</div>
              <div className="text-2xl font-bold text-sky-700 tabular-nums" data-testid="audit-remaining">{(totalParsed - committedCount).toLocaleString()}</div>
              <div className="text-[10px] text-sky-600/80">to review below</div>
            </div>
          </div>

          {/* Breakdown of the Remaining bucket — explains where the 'remaining' splits */}
          {(() => {
            const realKeys = ['biotech', 'delivery_tech', 'dtx', 'platform', 'university', 'pharma', 'distributor', 'investor'];
            const real = realKeys.reduce((sum, k) => sum + (displayBucketCounts[k]?.total || 0), 0);
            const noise = displayBucketCounts.excluded?.total || 0;
            const ambiguous = displayBucketCounts.skip_prompt?.total || 0;
            const approvedNow = displayBucketCounts.approved?.total || 0;
            const accountedFor = real + noise;
            const other = Math.max(totalRows - accountedFor, 0);
            return (
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-slate-600" data-testid="audit-breakdown">
                <span className="text-slate-400 font-medium uppercase tracking-wider text-[10px]">Of remaining →</span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span className="font-semibold text-slate-900 tabular-nums">{real.toLocaleString()}</span>
                  <span className="text-slate-500">real targets</span>
                  <span className="text-slate-400">(Biotech · Pharma · Investor · …)</span>
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-rose-400" />
                  <span className="font-semibold text-slate-900 tabular-nums">{noise.toLocaleString()}</span>
                  <span className="text-slate-500">noise</span>
                  <span className="text-slate-400">(CRO · Service · Unclear)</span>
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-amber-400" />
                  <span className="font-semibold text-slate-900 tabular-nums">{ambiguous.toLocaleString()}</span>
                  <span className="text-slate-500">needs your eye</span>
                  <span className="text-slate-400">(skip · prompt)</span>
                </span>
                {other > 0 && (
                  <span className="inline-flex items-center gap-1.5" title="Rows that don't fit the above three categories (e.g. uncategorized).">
                    <span className="w-2 h-2 rounded-full bg-slate-400" />
                    <span className="font-semibold text-slate-900 tabular-nums">{other.toLocaleString()}</span>
                    <span className="text-slate-500">other</span>
                  </span>
                )}
                <span className="ml-auto inline-flex items-center gap-2">
                  <button
                    type="button"
                    onClick={openDistribution}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100 hover:border-sky-400 transition-colors text-[11px] font-medium shadow-sm"
                    data-testid="open-distribution-btn"
                    title="See the full proposed_bucket × sub_category matrix — including already-committed rows. Useful for answering 'where did my biotechs-without-email actually go?'"
                  >
                    <BarChart3 className="w-3.5 h-3.5" />
                    Full distribution
                  </button>
                  <span className="inline-flex items-center gap-1.5 text-emerald-700">
                    <CheckCircle2 className="w-3 h-3" />
                    <span className="font-semibold tabular-nums" data-testid="audit-approved-now">{approvedNow.toLocaleString()}</span>
                    <span className="text-emerald-600/80">approved this batch · ready to commit</span>
                  </span>
                </span>
              </div>
            );
          })()}
        </div>
        {/* Default-approval rule card */}
        <div className="mt-3 bg-sky-50/60 border border-sky-200 rounded-md px-3 py-2 text-[11px] text-slate-700 flex items-start gap-2" data-testid="default-rule-banner">
          <CheckCircle2 className="w-3.5 h-3.5 text-sky-600 shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold text-sky-900">Default approval rule:</span> a row is auto-ticked if it has an email <span className="text-slate-500">(→ new record created)</span> OR if it fuzzy-matches an existing record <span className="text-slate-500">(→ existing record enriched with conference tag + missing fields)</span>. Rows with neither are auto-unticked so nothing contact-less leaks into your CRM. <span className="text-slate-500">Just click Commit when you&apos;re happy.</span>
          </div>
        </div>
      </div>

      <AutoImportPanel importTag={importTag} onDone={fetchData} />

      {assetJob && (
        <div className="mb-5 flex items-center justify-between gap-3 rounded-lg border border-fuchsia-200 bg-fuchsia-50 px-4 py-3" data-testid="companion-asset-import-banner">
          <div className="flex items-start gap-2 text-sm text-fuchsia-900">
            <FlaskConical className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <span className="font-semibold">{assetJob.total} asset rows</span> from this sheet are staged separately
              {' '}({assetJob.matched} matched · {assetJob.committed} committed).
              {committedCount === 0
                ? ' Commit the companies here first — the asset matcher re-runs automatically afterwards.'
                : ' Review and commit them to attach assets to their biotechs.'}
            </div>
          </div>
          <Link to={`/asset-imports/${importTag}`} data-testid="open-companion-asset-import">
            <Button size="sm" className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white">Open asset import</Button>
          </Link>
        </div>
      )}


      {/* Sticky toolbar */}
      <div className="sticky top-0 z-20 bg-slate-50/95 backdrop-blur-sm py-3 border-b border-slate-200 mb-3 -mx-6 md:-mx-8 px-6 md:px-8">
        <div className="flex items-center gap-3 flex-wrap mb-3">
          <Input
            placeholder="Search by name or country…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="h-9 w-72 text-sm"
            data-testid="import-search"
          />
          <div className="flex items-center gap-1 bg-white rounded-full border border-slate-200 p-0.5" data-testid="email-filter-group">
            {[
              { k: 'all', label: 'All' },
              { k: 'with', label: 'With email' },
              { k: 'without', label: 'No email' },
            ].map(opt => (
              <button
                key={opt.k}
                type="button"
                onClick={() => setEmailFilter(opt.k)}
                className={`text-xs px-3 py-1 rounded-full transition-colors ${emailFilter === opt.k ? 'bg-emerald-600 text-white font-semibold' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid={`email-filter-${opt.k}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Filter className="w-3.5 h-3.5 text-slate-400" />
            {BUCKETS.map(b => {
              const count = b.key === 'all' ? totalRows : (displayBucketCounts[b.key]?.total || 0);
              return (
                <button
                  key={b.key}
                  onClick={() => setFilter(b.key)}
                  className={`text-xs px-3 py-1 rounded-full transition-all border ${filter === b.key ? `${b.color} font-semibold shadow-sm border-transparent` : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}
                  data-testid={`bucket-filter-${b.key}`}
                >
                  {b.label} ({count})
                </button>
              );
            })}
          </div>
          <div className="flex-1" />
          {filter === 'stuck_no_email_no_match' && (
            <Button
              onClick={async () => {
                // Bulk-reclassify: default to selection if any, otherwise
                // fall back to the full visible view — matches the mental
                // model of the "Approve view" button next to it.
                const ids = selectedIds.size > 0
                  ? Array.from(selectedIds)
                  : filteredItems.map(r => r.id);
                if (ids.length === 0) {
                  toast.info('Nothing to reclassify.');
                  return;
                }
                if (!window.confirm(
                  `Reclassify ${ids.length} row${ids.length === 1 ? '' : 's'} to biotech (general_biotech) and auto-approve?\n\n` +
                  `Only run this on rows you're confident are biotechs (not real pharma / academic / distributor). ` +
                  `The Commit button afterwards will land them in biotech_companies.`
                )) return;
                try {
                  const r = await axios.post(
                    `${API}/imports/${importTag}/bulk-reclassify`,
                    { row_ids: ids, proposed_bucket: 'biotech', sub_category: 'general_biotech' },
                    { withCredentials: true },
                  );
                  toast.success(`Reclassified ${r.data?.modified || 0} row${r.data?.modified === 1 ? '' : 's'} → biotech + auto-approved`);
                  setSelectedIds(new Set());
                  await fetchData();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Bulk reclassify failed');
                }
              }}
              disabled={committing || filteredItems.length === 0}
              variant="outline"
              className="h-9 mr-2 border-rose-300 text-rose-700 hover:bg-rose-50"
              data-testid="bulk-reclassify-biotech-btn"
              title="Reclassify selected (or all visible if none selected) stuck rows to biotech (general_biotech) and auto-approve them. They'll then land in biotech_companies on the next Commit."
            >
              🧬 Reclassify → biotech ({selectedIds.size > 0 ? selectedIds.size : filteredItems.length})
            </Button>
          )}
          {filter === 'stuck_no_email_no_match' && (
            <div
              className="flex items-center gap-1.5 mr-2"
              data-testid="bulk-approve-controls"
              title="Bulk-approve stuck rows. Pick a bucket (or leave 'Keep original') then click Approve. Scope = ticked rows if any, else all visible."
            >
              <span className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Approve as</span>
              <select
                value={bulkBucket}
                onChange={e => setBulkBucket(e.target.value)}
                className="h-9 text-[11px] px-2 rounded border border-slate-300 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                data-testid="bulk-approve-bucket-select"
                disabled={bulkApproving}
              >
                <option value="">Keep original bucket</option>
                <option value="biotech">Biotech (general_biotech)</option>
                <option value="pharma">Pharma (partner)</option>
                <option value="academic">Academic</option>
                <option value="distributor">Distributor</option>
                <option value="investor">Investor</option>
              </select>
              <Button
                onClick={async () => {
                  const ids = selectedIds.size > 0
                    ? Array.from(selectedIds)
                    : filteredItems.map(r => r.id);
                  if (ids.length === 0) {
                    toast.info('Nothing to approve.');
                    return;
                  }
                  const label = bulkBucket
                    ? `${bulkBucket}${bulkBucket === 'biotech' ? ' (general_biotech)' : bulkBucket === 'pharma' ? ' (partner)' : ''}`
                    : 'their AI-classified bucket (kept)';
                  if (!window.confirm(
                    `Approve ${ids.length} row${ids.length === 1 ? '' : 's'} as ${label}?\n\n` +
                    (bulkBucket
                      ? `• Rows will be reclassified to ${bulkBucket}.\n`
                      : `• Each row keeps whatever bucket the AI gave it (biotech/pharma/academic/distributor/investor).\n`) +
                    `• All will be auto-approved and land in their respective collections on the next Commit.\n\n` +
                    `You can still un-tick / rollback per-row before Commit.`
                  )) return;
                  setBulkApproving(true);
                  try {
                    const body = { row_ids: ids };
                    if (bulkBucket) {
                      body.proposed_bucket = bulkBucket;
                      // Sensible default sub_categories per bucket.
                      const defSub = {
                        biotech: 'general_biotech',
                        pharma: 'partner',
                      }[bulkBucket];
                      if (defSub) body.sub_category = defSub;
                    }
                    // else: no `proposed_bucket` → backend treats as
                    // "keep each row's bucket, just approve".
                    const r = await axios.post(
                      `${API}/imports/${importTag}/bulk-reclassify`,
                      body,
                      { withCredentials: true },
                    );
                    toast.success(
                      bulkBucket
                        ? `Approved ${r.data?.modified || 0} row${r.data?.modified === 1 ? '' : 's'} as ${bulkBucket}.`
                        : `Approved ${r.data?.modified || 0} row${r.data?.modified === 1 ? '' : 's'} (buckets kept as-is).`
                    );
                    setSelectedIds(new Set());
                    setBulkBucket('');
                    await fetchData();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Bulk approve failed');
                  } finally {
                    setBulkApproving(false);
                  }
                }}
                disabled={bulkApproving || filteredItems.length === 0}
                variant="outline"
                className="h-9 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                data-testid="bulk-approve-btn"
              >
                {bulkApproving
                  ? '⏳ Approving…'
                  : `✅ Approve ${selectedIds.size > 0 ? selectedIds.size : filteredItems.length}`}
              </Button>
            </div>
          )}
          <Button
            onClick={downloadCurrentView}
            variant="outline"
            size="sm"
            className="h-9 mr-2 border-slate-300 text-slate-700 hover:bg-slate-50"
            data-testid="download-current-view-btn"
            title="Download the current view (active tab + email filter + search) as Excel. Useful for reviewing offline or feeding into other CRMs before approving."
          >
            <Download className="w-3.5 h-3.5 mr-1" /> Download view ({filteredItems.length})
          </Button>
          <Button
            onClick={async () => {
              const eligible = filteredItems.filter(r => !r.committed && !r.approved);
              if (eligible.length === 0) {
                toast.info('Nothing to approve in this view — all visible rows are already approved or committed.');
                return;
              }
              if (!window.confirm(
                `Approve all ${eligible.length} visible row${eligible.length === 1 ? '' : 's'} in this view?\n\n` +
                `These will be marked Approved and included on the next Commit. Already-committed rows are skipped automatically.`
              )) return;
              try {
                const r = await axios.post(
                  `${API}/imports/${importTag}/bulk-approve`,
                  { row_ids: eligible.map(x => x.id), approved: true },
                  { withCredentials: true },
                );
                toast.success(`Approved ${r.data?.modified || 0} of ${r.data?.requested || 0} visible rows`);
                await fetchData();
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Bulk approve failed');
              }
            }}
            disabled={committing || filteredItems.length === 0}
            variant="outline"
            className="h-9 mr-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="bulk-approve-view-btn"
            title="Approve every row currently visible in this filtered/searched view. Use after narrowing the table by bucket, email, or search."
          >
            ✓ Approve view ({filteredItems.filter(r => !r.committed && !r.approved).length})
          </Button>
          {filteredItems.some(r => r.approved && !r.committed) && (
            <Button
              onClick={async () => {
                const eligible = filteredItems.filter(r => !r.committed && r.approved);
                if (!window.confirm(`Unapprove all ${eligible.length} approved-but-uncommitted rows in this view?`)) return;
                try {
                  const r = await axios.post(
                    `${API}/imports/${importTag}/bulk-approve`,
                    { row_ids: eligible.map(x => x.id), approved: false },
                    { withCredentials: true },
                  );
                  toast.success(`Unapproved ${r.data?.modified || 0} rows`);
                  await fetchData();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Bulk unapprove failed');
                }
              }}
              disabled={committing}
              variant="outline"
              className="h-9 mr-2 border-slate-300 text-slate-600 hover:bg-slate-50"
              data-testid="bulk-unapprove-view-btn"
              title="Undo the approval for every approved-but-uncommitted row in this view. Already-committed rows are not affected."
            >
              ✗ Unapprove view
            </Button>
          )}
          <Button
            onClick={async () => {
              if (!window.confirm('Auto-approve every high-confidence row (biotech / pharma / academic / delivery tech / distributor)?\n\nThe AI-flagged low-confidence rows (Unclear / CRO/CDMO / name-only) are left for you to skim manually.')) return;
              try {
                const r = await axios.post(`${API}/imports/${importTag}/auto-approve`, null, { withCredentials: true });
                toast.success(r.data?.note || `Auto-approved ${r.data?.auto_approved || 0} rows`);
                await fetchData();
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Auto-approve failed');
              }
            }}
            disabled={committing}
            variant="outline"
            className="h-9 mr-2 border-sky-300 text-sky-700 hover:bg-sky-50"
            data-testid="auto-approve-btn"
            title="Bulk-approve every row the AI classified into a high-confidence bucket — skips Unclear / CRO/CDMO / name-only rows so you only manually review the ambiguous ones."
          >
            ✨ Auto-Approve High-Confidence
          </Button>
          <Button
            onClick={handleCommit}
            disabled={committing || approvedCount === 0}
            className="h-9 bg-emerald-600 text-white hover:bg-emerald-700"
            data-testid="commit-import-btn"
            title={approvedNoDest ? `${approvedNoDest} approved CRO/Service/Unclear row(s) have no CRM destination and will be skipped` : undefined}
          >
            {committing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Committing…</> : <><Save className="w-4 h-4 mr-2" /> Commit {committableCount}</>}
          </Button>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-slate-500">
          <span>Approved: <span className="font-semibold text-slate-700">{approvedCount}</span></span>
          <span>· {importCount} new imports</span>
          <span>· {enrichCount} enrichments</span>
          {approvedNoDest > 0 && (
            <span className="text-amber-700 font-medium" data-testid="approved-no-destination-note">· {approvedNoDest} approved CRO/Service/Unclear — no CRM destination, will be skipped</span>
          )}
        </div>
      </div>

      {/* Feb 2026 · Stuck-review help banner. Only shown when the "🚫 Stuck
          (no email + no match)" chip is active — walks the user through
          the reclassify-to-biotech + auto-approve + commit workflow so
          they don't have to guess how to rescue biotechs that were
          LLM-misclassified as pharma/academic/distributor. */}
      {filter === 'stuck_no_email_no_match' && !loading && (
        <div className="mb-3 bg-rose-50/70 border border-rose-200 rounded-md px-3 py-2.5 text-[11px] text-slate-700 flex items-start gap-2" data-testid="stuck-help-banner">
          <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-rose-900 mb-0.5">
              These rows were classified as pharma / academic / distributor by the AI, but they have neither an email nor an existing CRM match — so the default rule left them unapproved.
            </div>
            <div className="text-slate-600">
              If a row is actually a biotech (which auto-approves regardless of email), tick it and click <span className="font-semibold text-rose-800">🧬 Reclassify → biotech</span>. It&apos;ll be moved to <code className="bg-white px-1 rounded border border-slate-200">biotech</code> · <code className="bg-white px-1 rounded border border-slate-200">general_biotech</code>, auto-approved, and land in <code className="bg-white px-1 rounded border border-slate-200">biotech_companies</code> on the next Commit. Real pharma / universities can stay as-is or be manually approved.
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                {filter === 'stuck_no_email_no_match' && (
                  <th className="px-3 py-2 text-left w-8">
                    <input
                      type="checkbox"
                      checked={filteredItems.length > 0 && selectedIds.size === filteredItems.length}
                      onChange={e => {
                        if (e.target.checked) {
                          setSelectedIds(new Set(filteredItems.map(r => r.id)));
                        } else {
                          setSelectedIds(new Set());
                        }
                      }}
                      data-testid="stuck-select-all"
                      aria-label="Select all visible stuck rows"
                    />
                  </th>
                )}
                <th className="px-3 py-2 text-left">Company</th>
                <th className="px-3 py-2 text-left w-40">Bucket</th>
                <th className="px-3 py-2 text-left w-24">Country</th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left w-32">Decision</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map(r => {
                const dispKey = displayBucket(r);
                const bucketDef = BUCKETS.find(b => b.key === dispKey) || BUCKETS[0];
                const isExcluded = ['cro_cdmo', 'service', 'unclear'].includes(r.proposed_bucket);
                // Verbose exclusion reason — shows the user the exact rule that
                // hid this row + the offending field. Powers the "Why?" tooltip
                // on the Decision badge. Mirrors backend ordering (Rule 1 → 4).
                const exclusionReason = (() => {
                  if (r.approved) return null;
                  if (r.committed) return 'Already committed to CRM in a previous Commit pass.';
                  if (r.proposed_bucket === 'service') {
                    return 'Bucketed as Service (consulting / advisory / software vendor — not a BD target). To override, change the bucket dropdown to a real category.';
                  }
                  if (r.proposed_bucket === 'cro_cdmo') {
                    return 'Bucketed as CRO/CDMO (contract research or manufacturing org — does not own its own pipeline). To override, change the bucket dropdown.';
                  }
                  if (r.proposed_bucket === 'unclear') {
                    return 'Classifier had insufficient info (sector + description fields were blank in the source sheet) — needs human review. Click the company name to see the raw row.';
                  }
                  if (r.exclusion_filter_verdict === 'exclude') {
                    return `AI exclusion filter said EXCLUDE — ${r.exclusion_filter_reason || 'reason not stored'}.`;
                  }
                  if (r.biotech_filter_verdict === 'fail') {
                    return `AI biotech in-scope filter said FAIL — ${r.biotech_filter_reason || 'general-biotech prompt rejected this row'}.`;
                  }
                  if (r.pharma_filter_verdict === 'fail') {
                    return `AI pharma in-licensing filter said FAIL — ${r.pharma_filter_reason || 'no evidence of in-licensing intent in source data'}.`;
                  }
                  if (r.distributor_filter_verdict === 'fail') {
                    return `AI distributor in-scope filter said FAIL — ${r.distributor_filter_reason || 'looked like a pure logistics distributor with no in-licensing intent'}.`;
                  }
                  if (r.ndds_filter_verdict === 'fail') return 'Tagged as NDDS (novel drug delivery system) — historically excluded by the auto-rule because you pursue therapeutics not delivery platforms. Approve manually if you DO want this one.';
                  if (r.dtx_filter_verdict === 'fail') return 'Tagged as DTx (digital therapeutic). Auto-excluded for the same reason as NDDS — override manually if relevant.';
                  if (r.academic_filter_verdict === 'fail') return 'Academic / institutional row with no commercializable asset surface. Override manually if you want to keep the contact.';
                  if (r.sub_category === 'ndds' || r.sub_category === 'dtx' || r.sub_category === 'platform') {
                    return `Sub-category "${r.sub_category}" is auto-excluded by your default rule (you pursue therapeutics, not platforms / delivery / DTx). Click Approve to override.`;
                  }
                  if (r.action === 'enrich') return 'Enrichment-only — this row fuzzy-matches an existing CRM record. Approving will enrich the existing record (no duplicate created).';
                  if (!r.email && !r.existing_match) {
                    return 'No email in source sheet AND no fuzzy match to any existing CRM record. The auto-rule unticks this so contact-less leads do not silently leak in. Add an email manually or approve to create a no-contact stub.';
                  }
                  return 'Pending human review — no auto-rule matched.';
                })();
                return (
                  <tr
                    key={r.id}
                    ref={el => { if (el) rowRefs.current[r.id] = el; }}
                    className={`border-b border-slate-100 hover:bg-slate-50 ${r.approved ? 'bg-emerald-50/30' : ''} ${focusedId === r.id ? 'ring-2 ring-amber-400 bg-amber-50/60' : ''}`}
                    data-testid={`staging-row-${r.id}`}
                  >
                    {filter === 'stuck_no_email_no_match' && (
                      <td className="px-3 py-2 align-top">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(r.id)}
                          onChange={e => {
                            setSelectedIds(prev => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(r.id);
                              else next.delete(r.id);
                              return next;
                            });
                          }}
                          data-testid={`stuck-select-${r.id}`}
                          aria-label={`Select ${r.name}`}
                        />
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <div className="flex flex-col min-w-0">
                        <button
                          type="button"
                          onClick={() => setDetailRow(r)}
                          className="text-left font-semibold text-slate-900 hover:text-sky-700 hover:underline transition-colors"
                          data-testid={`company-name-btn-${r.id}`}
                        >
                          {r.name}
                          {r.hallucination_flag && (
                            <span className="ml-2 text-[9px] font-medium bg-rose-50 text-rose-600 px-1.5 py-0.5 rounded border border-rose-200 align-middle" title="Only the company name was in the source sheet — no sector, website, or email. Original classification is likely unreliable.">
                              ⚠ name-only in source
                            </span>
                          )}
                          {r.reclassification_source === 'claude_audit' && (
                            <span className="ml-2 text-[9px] font-medium bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded border border-indigo-200 align-middle" title={`Reclassified by Claude audit from ${r.original_bucket_before_audit || '?'} → ${r.proposed_bucket}. Reason: ${r.reclassification_note || ''}. Change bucket below if you disagree.`}>
                              ↺ audited: {r.original_bucket_before_audit || '?'} → {r.proposed_bucket}
                            </span>
                          )}
                          {r.classification_source === 'perplexity' && r.perplexity_proposal && (
                            <span
                              className="ml-2 text-[9px] font-medium bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-200 align-middle"
                              title={`Web-validated by Perplexity from ${r.claude_bucket_before_pplx || '?'} → ${r.proposed_bucket} (${r.perplexity_proposal.confidence}). ${r.perplexity_proposal.rationale || ''}${r.perplexity_proposal.url ? '\n\nSource: ' + r.perplexity_proposal.url : ''}`}
                            >
                              🌐 web: {r.claude_bucket_before_pplx || '?'} → {r.proposed_bucket}
                            </span>
                          )}
                          {(() => {
                            const v = r.academic_filter_verdict || r.ndds_filter_verdict || r.dtx_filter_verdict || r.biotech_filter_verdict || r.pharma_filter_verdict;
                            const reason = r.academic_filter_reason || r.ndds_filter_reason || r.dtx_filter_reason || r.biotech_filter_reason || r.pharma_filter_reason || '';
                            const which = r.academic_filter_verdict ? 'academic'
                              : r.ndds_filter_verdict ? 'NDD'
                              : r.dtx_filter_verdict ? 'DTx'
                              : r.biotech_filter_verdict ? 'biotech'
                              : r.pharma_filter_verdict ? 'pharma' : '';
                            if (!v) return null;
                            return v === 'pass' ? (
                              <span className="ml-2 text-[9px] font-medium bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-200 align-middle" title={`Passes ${which} canonical inclusion prompt. ${reason}`}>
                                ✓ {which} prompt
                              </span>
                            ) : (
                              <span className="ml-2 text-[9px] font-medium bg-orange-50 text-orange-700 px-1.5 py-0.5 rounded border border-orange-200 align-middle" title={`Fails ${which} prompt — ${reason}. Override by editing the bucket below if you disagree.`}>
                                ✗ {which}: {reason.length > 40 ? reason.slice(0, 40) + '…' : reason}
                              </span>
                            );
                          })()}
                          {r.exclusion_filter_verdict === 'exclude' && (
                            <span className="ml-2 text-[9px] font-medium bg-rose-50 text-rose-700 px-1.5 py-0.5 rounded border border-rose-200 align-middle" title={`Excluded by service-provider rule (${r.exclusion_filter_reason_code}): ${r.exclusion_filter_reason_text || ''}. Override via bucket dropdown if you disagree.`}>
                              ⊘ {(r.exclusion_filter_reason_code || '').replace(/_/g, ' ')}
                            </span>
                          )}
                        </button>
                        {r.description && (
                          <span className="text-[10px] text-slate-400 mt-0.5 line-clamp-2 max-w-md">{r.description}</span>
                        )}
                        {r.existing_match && (
                          <span className="text-[10px] text-amber-700 mt-0.5 flex items-center gap-1">
                            <CheckCircle2 className="w-2.5 h-2.5" />
                            Matched: <span className="font-medium">{r.existing_match.matched_name}</span>
                            <span className="text-slate-400">({r.existing_match.match_type}, {r.existing_match.score}%)</span>
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <Select value={dispKey} onValueChange={v => updateRow(r.id, splitDisplayBucket(v))}>
                        <SelectTrigger className={`h-7 text-[11px] border-0 ${bucketDef.color} font-semibold w-full`} data-testid={`bucket-select-${r.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {BUCKETS.filter(b => b.key !== 'all').map(b => (
                            <SelectItem key={b.key} value={b.key}>{b.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-2.5 h-2.5 text-slate-400" />
                        {r.country || '—'}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {r.email ? (
                        <span className="flex items-center gap-1 text-slate-700">
                          <Mail className="w-2.5 h-2.5 text-emerald-600" />
                          {r.email}
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {/* Decision cell: badge above + single Approve/Revert action below */}
                      <div className="flex flex-col gap-1.5 items-start">
                        {r.approved ? (
                          <Badge className="bg-emerald-600 text-white text-[10px]" data-testid={`decision-${r.id}`}>✓ Approved</Badge>
                        ) : isExcluded ? (
                          <Badge className="bg-rose-100 text-rose-700 text-[10px]" title={exclusionReason || 'Excluded'} data-testid={`decision-${r.id}`}>Excluded</Badge>
                        ) : r.exclusion_filter_verdict === 'exclude' ? (
                          <Badge className="bg-rose-100 text-rose-700 text-[10px]" title={exclusionReason} data-testid={`decision-${r.id}`}>Excluded · AI</Badge>
                        ) : (r.academic_filter_verdict === 'fail' || r.ndds_filter_verdict === 'fail' || r.dtx_filter_verdict === 'fail' || r.biotech_filter_verdict === 'fail' || r.pharma_filter_verdict === 'fail' || r.distributor_filter_verdict === 'fail') ? (
                          <Badge className="bg-orange-100 text-orange-700 text-[10px]" title={exclusionReason} data-testid={`decision-${r.id}`}>Skip · prompt</Badge>
                        ) : r.action === 'enrich' ? (
                          <Badge className="bg-amber-100 text-amber-800 text-[10px]" title={exclusionReason} data-testid={`decision-${r.id}`}>Will enrich</Badge>
                        ) : !r.email && !r.existing_match ? (
                          <Badge className="bg-slate-100 text-slate-500 text-[10px]" title={exclusionReason} data-testid={`decision-${r.id}`}>Skip · no email</Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-700 text-[10px]" title={exclusionReason || 'Pending'} data-testid={`decision-${r.id}`}>Pending</Badge>
                        )}
                        {/* Hover-explain the reason inline — the 1-liner stays visible so the user doesn't have to learn the exclusion-rule encyclopedia. Click for full popover. */}
                        {!r.approved && exclusionReason && (
                          <button
                            type="button"
                            onClick={() => alert(`Why "${r.name}" isn't auto-approved:\n\n${exclusionReason}`)}
                            className="text-[9px] text-slate-400 hover:text-slate-600 underline-offset-2 hover:underline truncate max-w-[200px] text-left"
                            title={exclusionReason}
                            data-testid={`why-${r.id}`}
                          >
                            ⓘ Why?
                          </button>
                        )}
                        {r.approved ? (
                          <button
                            type="button"
                            onClick={() => updateRow(r.id, { approved: false })}
                            className="text-[10px] text-slate-500 hover:text-rose-600 underline underline-offset-2"
                            data-testid={`revert-btn-${r.id}`}
                          >
                            Revert
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => updateRow(r.id, { approved: true })}
                            className="text-[10px] font-semibold text-emerald-700 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100 px-2 py-0.5 rounded border border-emerald-200"
                            data-testid={`approve-btn-${r.id}`}
                          >
                            Approve
                          </button>
                        )}
                        {savingId === r.id && <Loader2 className="w-2.5 h-2.5 animate-spin text-slate-400" />}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredItems.length === 0 && (
            <div className="py-12 text-center text-slate-400 text-xs">No rows match the current filter.</div>
          )}
        </div>
      )}

      {/* Detail modal — full description + enrichment preview */}
      <DetailModal row={detailRow} onClose={() => setDetailRow(null)} />

      {/* Distribution modal — full bucket × sub_category × committed × email matrix */}
      <DistributionModal
        open={distOpen}
        loading={distLoading}
        data={distData}
        onClose={() => setDistOpen(false)}
      />

      <Dialog open={confirmCommitOpen} onOpenChange={setConfirmCommitOpen}>
        <DialogContent data-testid="commit-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Commit {committableCount} approved rows?</DialogTitle>
            <DialogDescription>
              This will write all approved rows from <span className="font-semibold text-slate-700">{importTag}</span> into your CRM.
              New companies will be created and matched ones will be enriched.
              {approvedNoDest > 0 && (
                <span className="block mt-2 rounded-md bg-amber-50 border border-amber-200 px-2.5 py-2 text-amber-800" data-testid="commit-no-destination-warning">
                  <span className="font-semibold">{approvedNoDest} approved row{approvedNoDest === 1 ? '' : 's'} will be skipped</span> — CRO/CDMO, Service and Unclear
                  buckets have no CRM destination (out of licensing scope). Change the bucket to Biotech / Pharma / Distributor / Investor first if they belong in the CRM.
                </span>
              )}
              <span className="block mt-2 text-rose-600">This action cannot be undone.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmCommitOpen(false)} data-testid="commit-cancel-btn">Cancel</Button>
            <Button onClick={doCommit} disabled={committableCount === 0} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="commit-confirm-btn">
              <Save className="w-4 h-4 mr-2" /> Commit {committableCount} approved
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function DetailModal({ row, onClose }) {
  const [crmRecord, setCrmRecord] = React.useState(null);
  React.useEffect(() => {
    if (!row?.existing_match?.id) { setCrmRecord(null); return; }
    const m = row.existing_match;
    const targetCol = (row.proposed_bucket === 'investor') ? 'investors'
      : (row.proposed_bucket === 'pharma') ? 'company_profiles'
      : 'biotech_companies';
    axios.get(`${API}/imports/lookup-record?collection=${targetCol}&id=${m.id}`, { withCredentials: true })
      .then(r => setCrmRecord(r.data))
      .catch(() => setCrmRecord(null));
  }, [row]);

  if (!row) return null;
  const m = row.existing_match;
  const isEnrich = !!m;

  // Compute the diff: which fields will be filled from the staging row
  const enrichFields = isEnrich && crmRecord ? [
    { label: 'Description', staging: row.description, current: crmRecord.description, key: 'description' },
    { label: 'Website', staging: row.website, current: crmRecord.website, key: 'website' },
    { label: 'Country', staging: row.country, current: crmRecord.country, key: 'country' },
    { label: 'LinkedIn', staging: row.linkedin, current: crmRecord.linkedin, key: 'linkedin' },
    { label: 'Email', staging: row.email, current: crmRecord.contact_email || crmRecord.email, key: 'email' },
  ] : [];

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="row-detail-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mt-12 mb-12" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{row.name}</h3>
            <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-500 flex-wrap">
              <span className="font-medium text-slate-700 capitalize">{(BUCKETS.find(b => b.key === displayBucket(row)) || {}).label}</span>
              {row.country && <span>· {row.country}</span>}
              {row.email && <span className="text-emerald-700">· {row.email}</span>}
              {isEnrich && <span className="text-amber-700">· will enrich {m.matched_name}</span>}
              {!isEnrich && row.approved && <span className="text-emerald-700">· will create new record</span>}
              {!row.approved && <span className="text-rose-600">· skipped on commit</span>}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none" data-testid="modal-close">×</button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Source meta */}
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div><div className="text-[10px] uppercase tracking-wider text-slate-400">Source sector</div><div className="text-slate-700 mt-0.5">{row.original_sector || <span className="text-slate-300">— not provided</span>}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-400">Website</div><div className="text-slate-700 mt-0.5 truncate">{row.website ? <a href={row.website.startsWith('http') ? row.website : `https://${row.website}`} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline">{row.website}</a> : <span className="text-slate-300">—</span>}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-400">LinkedIn</div><div className="text-slate-700 mt-0.5 truncate">{row.linkedin ? <a href={row.linkedin} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline">profile</a> : <span className="text-slate-300">—</span>}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-400">Employees</div><div className="text-slate-700 mt-0.5">{row.employees || <span className="text-slate-300">—</span>}</div></div>
          </div>

          {/* Description */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Description</div>
            <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="modal-description">
              {row.description || <span className="text-slate-300 italic">No description in source sheet.</span>}
            </div>
          </div>

          {/* Match details */}
          {isEnrich && (
            <div className="rounded-md border border-amber-200 bg-amber-50/40 px-4 py-3">
              <div className="text-[10px] uppercase tracking-wider text-amber-700 mb-2 font-semibold">Enrichment preview</div>
              <div className="text-xs text-slate-700 mb-3">
                Matched to existing CRM record <span className="font-semibold">{m.matched_name}</span> via {m.match_type} ({m.score}%). On commit, the existing record will be tagged <code className="text-[10px] bg-amber-100 px-1 rounded">met_at: {row.import_tag}</code> and these fields will be backfilled <span className="text-slate-500">(only blank fields are touched, never overwriting existing data)</span>:
              </div>
              {!crmRecord && <div className="text-[11px] text-slate-400 italic">Loading existing CRM record…</div>}
              {crmRecord && (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[9px] uppercase tracking-wider text-slate-400">
                      <th className="text-left py-1">Field</th>
                      <th className="text-left py-1">Existing value (CRM)</th>
                      <th className="text-left py-1">From this row</th>
                      <th className="text-right py-1 w-20">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-amber-200/40">
                    {enrichFields.map(f => {
                      const willFill = !f.current && !!f.staging;
                      const noChange = !!f.current;
                      const noData = !f.current && !f.staging;
                      return (
                        <tr key={f.key} className="align-top">
                          <td className="py-1.5 pr-2 text-slate-500 font-medium">{f.label}</td>
                          <td className="py-1.5 pr-2 text-slate-700 break-words max-w-[200px]">{f.current || <span className="text-slate-300">—</span>}</td>
                          <td className="py-1.5 pr-2 text-slate-700 break-words max-w-[200px]">{f.staging || <span className="text-slate-300">—</span>}</td>
                          <td className="py-1.5 text-right">
                            {willFill ? <span className="text-emerald-700 text-[10px] font-semibold">+ fill</span>
                              : noChange ? <span className="text-slate-400 text-[10px]">keep</span>
                              : noData ? <span className="text-slate-300 text-[10px]">—</span> : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Filter / classification flags */}
          <div className="text-[11px] text-slate-500 space-y-1.5">
            {row.hallucination_flag && (
              <div className="flex items-start gap-2"><span className="text-rose-600">⚠</span><span>Source sheet had only a company name — no sector, website, or email. Classification is unreliable.</span></div>
            )}
            {row.reclassification_source === 'claude_audit' && (
              <div className="flex items-start gap-2"><span className="text-indigo-600">↺</span><span>Reclassified by Claude audit from <span className="font-medium">{row.original_bucket_before_audit}</span> → <span className="font-medium">{row.proposed_bucket}</span>: {row.reclassification_note}</span></div>
            )}
            {row.exclusion_filter_verdict === 'exclude' && (
              <div className="flex items-start gap-2"><span className="text-rose-600">⊘</span><span>Excluded by service-provider rule ({row.exclusion_filter_reason_code}): {row.exclusion_filter_reason_text}</span></div>
            )}
            {(row.academic_filter_verdict || row.ndds_filter_verdict || row.dtx_filter_verdict || row.biotech_filter_verdict) && (
              <div className="flex items-start gap-2">
                <span className={(row.academic_filter_verdict || row.ndds_filter_verdict || row.dtx_filter_verdict || row.biotech_filter_verdict) === 'pass' ? 'text-emerald-600' : 'text-orange-600'}>
                  {(row.academic_filter_verdict || row.ndds_filter_verdict || row.dtx_filter_verdict || row.biotech_filter_verdict) === 'pass' ? '✓' : '✗'}
                </span>
                <span>Inclusion-prompt verdict: {row.academic_filter_reason || row.ndds_filter_reason || row.dtx_filter_reason || row.biotech_filter_reason}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// DistributionModal
// Shows the full proposed_bucket × sub_category × committed × email matrix
// so the user can answer: "where did my biotechs-without-email actually go?"
// Pulls from GET /imports/{tag}/distribution.
// ─────────────────────────────────────────────────────────────────────────────
function DistributionModal({ open, loading, data, onClose }) {
  if (!open) return null;
  const rows = data?.matrix || [];
  const grandTotal = data?.grand_total || 0;

  // Column subtotals — committed vs pending, with vs without email
  const totals = rows.reduce((acc, r) => {
    acc.cwe += r.committed_with_email;
    acc.cne += r.committed_no_email;
    acc.pwe += r.pending_with_email;
    acc.pne += r.pending_no_email;
    return acc;
  }, { cwe: 0, cne: 0, pwe: 0, pne: 0 });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="distribution-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-sky-600" />
            Full distribution — every row, by classification and state
          </DialogTitle>
          <DialogDescription>
            Answers <em>&ldquo;where did my biotechs-without-email actually go?&rdquo;</em> — including the rows
            already committed. Each row is one (<span className="font-mono text-[10px]">proposed_bucket</span> ×{' '}
            <span className="font-mono text-[10px]">sub_category</span>) cell.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-500">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading distribution…
          </div>
        ) : (
          <div className="overflow-auto border border-slate-200 rounded-md">
            <table className="w-full text-xs" data-testid="distribution-table">
              <thead className="bg-slate-50 text-slate-600 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Bucket</th>
                  <th className="text-left px-3 py-2 font-semibold">Sub-category</th>
                  <th className="text-right px-3 py-2 font-semibold text-emerald-700" title="Already committed to CRM; row had an email when committed.">
                    Committed<br /><span className="text-[10px] font-normal text-emerald-600/70">+ email</span>
                  </th>
                  <th className="text-right px-3 py-2 font-semibold text-emerald-700" title="Already committed via fuzzy-match enrichment; row had no email.">
                    Committed<br /><span className="text-[10px] font-normal text-emerald-600/70">no email</span>
                  </th>
                  <th className="text-right px-3 py-2 font-semibold text-sky-700" title="Still in staging, waiting for your approve / commit. Has an email.">
                    Pending<br /><span className="text-[10px] font-normal text-sky-600/70">+ email</span>
                  </th>
                  <th className="text-right px-3 py-2 font-semibold text-amber-700" title="Still in staging with NO email. These are the rows that auto-uncheck — the ones you wanted to see counted.">
                    Pending<br /><span className="text-[10px] font-normal text-amber-600/70">no email</span>
                  </th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-900">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100 hover:bg-slate-50/60">
                    <td className="px-3 py-1.5 font-medium text-slate-800">{r.bucket}</td>
                    <td className="px-3 py-1.5 text-slate-600 font-mono text-[11px]">{r.sub_category}</td>
                    <td className="text-right px-3 py-1.5 tabular-nums">{r.committed_with_email || ''}</td>
                    <td className="text-right px-3 py-1.5 tabular-nums">{r.committed_no_email || ''}</td>
                    <td className="text-right px-3 py-1.5 tabular-nums">{r.pending_with_email || ''}</td>
                    <td className="text-right px-3 py-1.5 tabular-nums text-amber-700 font-semibold">{r.pending_no_email || ''}</td>
                    <td className="text-right px-3 py-1.5 tabular-nums font-semibold text-slate-900">{r.total}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-8 text-slate-400">No rows.</td></tr>
                )}
              </tbody>
              <tfoot className="bg-slate-50 border-t-2 border-slate-300">
                <tr>
                  <td className="px-3 py-2 font-bold text-slate-900" colSpan={2}>TOTAL</td>
                  <td className="text-right px-3 py-2 tabular-nums font-bold text-emerald-700">{totals.cwe.toLocaleString()}</td>
                  <td className="text-right px-3 py-2 tabular-nums font-bold text-emerald-700">{totals.cne.toLocaleString()}</td>
                  <td className="text-right px-3 py-2 tabular-nums font-bold text-sky-700">{totals.pwe.toLocaleString()}</td>
                  <td className="text-right px-3 py-2 tabular-nums font-bold text-amber-700">{totals.pne.toLocaleString()}</td>
                  <td className="text-right px-3 py-2 tabular-nums font-bold text-slate-900">{grandTotal.toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        <DialogFooter>
          <p className="text-[11px] text-slate-500 mr-auto leading-relaxed">
            <span className="font-semibold text-amber-700">Pending · no email</span> is the column you asked about —
            these are the rows that stay unchecked by default. If <span className="font-mono text-[10px]">biotech · general_biotech</span> shows zero
            here, then no general-biotechs were found without an email. They may have been mis-bucketed (check <span className="font-mono text-[10px]">cro_cdmo</span>,{' '}
            <span className="font-mono text-[10px]">service</span>, <span className="font-mono text-[10px]">unclear</span>).
          </p>
          <Button variant="outline" onClick={onClose} data-testid="distribution-close-btn">
            <X className="w-3.5 h-3.5 mr-1" /> Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
